import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";
import type { usePersonalEvents } from "@/hooks/usePersonalEvents";
import type { GoogleCalendarApi } from "@/hooks/useGoogleCalendar";
import type { UserCalendarsApi } from "@/hooks/useUserCalendars";
import { DEFAULT_CALENDAR, DEFAULT_CALENDAR_ID, storeKeyErp, storeKeyGoogle } from "@/domain/calendar";
import {
  toEventInput,
  type PersonalEvent,
  type PersonalEventInput,
  type PersonalEventKind,
} from "@/domain/personalEvent";
import { canWriteCalendar, type GoogleEvent } from "@/domain/googleCalendar";
import { PersonalEventModal, type EventSaveTarget } from "./PersonalEventModal";

/**
 * 개인 일정·할 일 편집 — 추가·수정 모달, 삭제 확인, 완료 토글을 한 벌로 (B′ 미리보기 2차).
 * 내 업무 홈(index.tsx)과 일정·할 일(Tasks.tsx)이 같은 모달·문구를 쓰도록 묶었다.
 *
 * gcal(선택, 2026-10-23): Google 캘린더가 연결돼 있으면 새 일정의 저장 위치에 쓰기 가능한 캘린더가 붙고,
 *   openGoogle(ev) 로 Google 일정을 같은 모달에서 고치거나 지운다. 마지막에 고른 위치는 이 화면에서 기억한다.
 * cal(선택, 2026-10-29): 내 캘린더 목록 — 저장 위치 그룹 + 일정의 calendar_id. 없으면 기본 캘린더만.
 */
type Target =
  | { mode: "new"; date: string; kind: PersonalEventKind }
  | { mode: "edit"; ev: PersonalEvent }
  | { mode: "google"; ev: GoogleEvent }
  | null;

const LABEL: Record<PersonalEventKind, string> = { event: "일정", todo: "할 일" };

export function useEventEditor(
  pe: ReturnType<typeof usePersonalEvents>,
  defaultDate: string,
  holidayLabel?: (ymd: string) => string | null,
  gcal?: GoogleCalendarApi,
  cal?: UserCalendarsApi,
) {
  const confirm = useConfirm();
  const toast = useToast();
  const [target, setTarget] = useState<Target>(null);
  const [lastStore, setLastStore] = useState(storeKeyErp(DEFAULT_CALENDAR_ID));

  const gMode = gcal?.mode;
  const gImported = gcal?.imported;
  const writable = useMemo(
    () => (gMode === "linked" && gImported ? gImported.filter(canWriteCalendar) : []),
    [gMode, gImported],
  );
  const userCalendars = cal?.calendars ?? [DEFAULT_CALENDAR];

  const openNew = useCallback(
    (date?: string, kind: PersonalEventKind = "event") => setTarget({ mode: "new", date: date ?? defaultDate, kind }),
    [defaultDate],
  );
  const openEdit = useCallback((ev: PersonalEvent) => setTarget({ mode: "edit", ev }), []);
  const openGoogle = useCallback((ev: GoogleEvent) => setTarget({ mode: "google", ev }), []);
  const close = useCallback(() => setTarget(null), []);

  async function save(input: PersonalEventInput, to: EventSaveTarget) {
    if (target?.mode === "google") {
      if (!gcal) throw new Error("Google 캘린더 연결을 확인하세요.");
      await gcal.update(target.ev, input, to.store === "google" && to.memoChanged);
      toast.success("Google 일정을 수정했습니다.");
    } else if (target?.mode === "edit") {
      await pe.update(target.ev.id, input);
      toast.success(`${LABEL[input.kind]}을 수정했습니다.`);
    } else if (to.store === "google") {
      if (!gcal) throw new Error("Google 캘린더 연결을 확인하세요.");
      await gcal.create(to.calendarId, input);
      setLastStore(storeKeyGoogle(to.calendarId));
      toast.success(`Google 캘린더(${gcal.calendarOf(to.calendarId)?.name ?? ""})에 추가했습니다.`);
    } else {
      await pe.add(input);
      if (input.kind === "event") setLastStore(storeKeyErp(to.calendarId));
      const name = to.calendarId !== DEFAULT_CALENDAR_ID ? cal?.calendarOf(to.calendarId).name : "";
      toast.success(`${LABEL[input.kind]}을 추가했습니다.${name ? ` (${name})` : ""}`);
    }
  }

  async function del(ev: PersonalEvent) {
    const ok = await confirm({
      title: `${LABEL[ev.kind]}을 삭제할까요?`,
      summary: `'${ev.title}' · ${ev.date}`,
      irreversible: true,
      confirmLabel: "삭제",
      kind: "danger",
    });
    if (!ok) return;
    try {
      await pe.remove(ev.id);
      setTarget(null);
      toast.success(`${LABEL[ev.kind]}을 삭제했습니다.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "삭제하지 못했습니다.");
    }
  }

  async function delGoogle(ev: GoogleEvent) {
    if (!gcal) return;
    const ok = await confirm({
      title: "Google 일정을 삭제할까요?",
      summary: `'${ev.title}' · ${ev.date}${ev.recurring ? " (반복 일정 중 이 회차만)" : ""} — 아이폰·Google 캘린더에서도 지워집니다.`,
      irreversible: true,
      confirmLabel: "삭제",
      kind: "danger",
    });
    if (!ok) return;
    try {
      await gcal.remove(ev);
      setTarget(null);
      toast.success("Google 일정을 삭제했습니다.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "삭제하지 못했습니다.");
    }
  }

  const toggleDone = useCallback(
    async (ev: PersonalEvent) => {
      try {
        await pe.update(ev.id, { ...toEventInput(ev), done: !ev.done });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "저장하지 못했습니다.");
      }
    },
    [pe, toast],
  );

  let modal: ReactNode = null;
  if (target?.mode === "google") {
    modal = (
      <PersonalEventModal
        key={`g-${target.ev.id}`}
        googleEvent={target.ev}
        googleCalendar={gcal?.calendarOf(target.ev.calendar_id)}
        defaultDate={defaultDate}
        holidayLabel={holidayLabel}
        onSave={save}
        onDelete={() => void delGoogle(target.ev)}
        onClose={close}
      />
    );
  } else if (target) {
    modal = (
      <PersonalEventModal
        key={target.mode === "edit" ? target.ev.id : `new-${target.kind}-${target.date}`}
        event={target.mode === "edit" ? target.ev : undefined}
        defaultDate={target.mode === "new" ? target.date : defaultDate}
        defaultKind={target.mode === "new" ? target.kind : undefined}
        userCalendars={userCalendars}
        writableCalendars={target.mode === "new" ? writable : undefined}
        defaultStore={lastStore}
        holidayLabel={holidayLabel}
        onSave={save}
        onDelete={target.mode === "edit" ? () => void del(target.ev) : undefined}
        onClose={close}
      />
    );
  }

  return { openNew, openEdit, openGoogle, toggleDone, modal };
}
